import React from 'react';
import { UiSummaryCard, UiSummaryCards } from '../../components/ui/UiSummaryCard';
import { UiViewToggle } from '../../components/ui/UiTabs';

// Profit & Loss report tab — extracted from Reports.js. Includes the expense-based
// P&L sub-view. Presentational; data/handlers via ctx.
export default function ProfitReport(ctx) {
  const { data, formatCurrency, profitMode, setProfitMode, expensePLData, expensePLLoading, expensePLPeriod, setExpensePLPeriod, setDepartmentFilter } = ctx;
    const renderExpenseBasedPL = () => {
      const d = expensePLData;
      if (expensePLLoading) return (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
        </div>
      );
      if (!d) return <p className="text-sm text-gray-500 text-center py-8">No data available</p>;
      const s = d.summary || {};
      return (
        <div className="space-y-6">
          {/* Period toggle */}
          <div className="flex items-center gap-2">
            {['monthly', 'yearly'].map((p) => (
              <button key={p} onClick={() => setExpensePLPeriod(p)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition ${expensePLPeriod === p ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
              >{p === 'monthly' ? 'Monthly' : 'Yearly'}</button>
            ))}
          </div>

          {d.department_name && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2">
              <p className="text-sm font-medium text-blue-800">Department: {d.department_name}</p>
            </div>
          )}

          {/* Summary Cards */}
          <UiSummaryCards cols="grid-cols-2 sm:grid-cols-4">
            <UiSummaryCard label="Revenue (Sales)" value={formatCurrency(s.total_revenue)} tone="text-blue-600" />
            <UiSummaryCard label="Total Expenses (Cost)" value={formatCurrency(s.total_expenses)} tone="text-red-600" />
            <UiSummaryCard label="Net Profit" value={formatCurrency(s.net_profit)} tone={(s.net_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'} />
            <UiSummaryCard label="Net Margin" value={`${s.net_margin || 0}%`} />
          </UiSummaryCards>

          {/* Expense Breakdown */}
          {d.expense_breakdown?.length > 0 && (
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border">
              <h3 className="text-base sm:text-lg font-semibold mb-4">
                {d.department_name ? `${d.department_name} Expenses` : 'Expense Breakdown'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {d.expense_breakdown.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                    <p className="font-medium text-gray-800 capitalize">{item.category_display || item.category?.replace('_', ' ')}</p>
                    <p className="font-bold text-red-700">{formatCurrency(item.total)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Storewide Expenses (shown separately when viewing a department) */}
          {d.mode === 'department' && d.storewide_expense_breakdown?.length > 0 && (
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border">
              <h3 className="text-base sm:text-lg font-semibold mb-2">Store-wide Expenses</h3>
              <p className="text-sm text-gray-500 mb-4">These expenses are not assigned to any department</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {d.storewide_expense_breakdown.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="font-medium text-gray-800 capitalize">{item.category_display || item.category?.replace('_', ' ')}</p>
                    <p className="font-bold text-amber-700">{formatCurrency(item.total)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-amber-200 flex justify-between">
                <p className="font-medium text-gray-700">Total Store-wide</p>
                <p className="font-bold text-amber-700">{formatCurrency(s.storewide_expenses)}</p>
              </div>
            </div>
          )}

          {/* Department Breakdown (storewide mode) */}
          {d.mode === 'storewide' && d.by_department?.length > 0 && (
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border">
              <h3 className="text-base sm:text-lg font-semibold mb-4">By Department</h3>
              <div className="space-y-3">
                {d.by_department.map((dept, i) => (
                  <div key={i}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border cursor-pointer hover:bg-gray-100 transition"
                    onClick={() => setDepartmentFilter(String(dept.department_id))}
                  >
                    <div>
                      <p className="font-medium text-gray-800">{dept.department_name}</p>
                      <p className="text-xs text-gray-500">Revenue: {formatCurrency(dept.revenue)} | Expenses: {formatCurrency(dept.expenses)}</p>
                    </div>
                    <p className={`font-bold ${dept.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(dept.profit)}</p>
                  </div>
                ))}
              </div>
              {(d.storewide_expenses || 0) > 0 && (
                <div className="mt-4 pt-3 border-t flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div>
                    <p className="font-medium text-gray-800">Store-wide Expenses</p>
                    <p className="text-xs text-gray-500">Not assigned to any department</p>
                  </div>
                  <p className="font-bold text-amber-700">{formatCurrency(d.storewide_expenses)}</p>
                </div>
              )}
            </div>
          )}
        </div>
      );
    };
  return (
    <div className="space-y-6">
      {/* Inventory / Expense Toggle */}
      <UiViewToggle
        options={[{ value: 'inventory', label: 'Inventory' }, { value: 'expense', label: 'Expense Based' }]}
        value={profitMode}
        onChange={setProfitMode}
      />

      {profitMode === 'expense' ? renderExpenseBasedPL() : (
        <>
          {/* Summary */}
          <UiSummaryCards cols="grid-cols-3">
            <UiSummaryCard label="Revenue" value={formatCurrency(data?.summary?.total_revenue)} tone="text-blue-600" />
            <UiSummaryCard label="Cost" value={formatCurrency(data?.summary?.total_cost)} tone="text-red-600" />
            <UiSummaryCard label="Gross Profit" value={formatCurrency(data?.summary?.gross_profit)} tone="text-green-600" />
          </UiSummaryCards>

          <UiSummaryCards cols="grid-cols-2 sm:grid-cols-4">
            <UiSummaryCard label="Wastage" value={formatCurrency(data?.summary?.wastage_cost)} tone="text-orange-600" />
            <UiSummaryCard label="Expenses" value={formatCurrency(data?.summary?.total_expenses)} tone="text-red-600"
              sub={data?.summary?.distributed_fixed_expenses > 0 ? `Incl. ${formatCurrency(data.summary.distributed_fixed_expenses)} fixed (distributed)` : ''} />
            <UiSummaryCard label="Net Profit" value={formatCurrency(data?.summary?.net_profit)} tone={(data?.summary?.net_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'} />
            <UiSummaryCard label="Net Margin" value={`${data?.summary?.net_margin || 0}%`} />
          </UiSummaryCards>

          {/* Expense Breakdown in P&L */}
          {data?.expense_by_category?.length > 0 && (
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border">
              <h3 className="text-base sm:text-lg font-semibold mb-4">Expense Breakdown</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.expense_by_category.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                    <p className="font-medium text-gray-800">{item.category_display || item.category?.replace('_', ' ')}</p>
                    <p className="font-bold text-red-700">{formatCurrency(item.total)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Profit by Item */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden overflow-x-auto">
            <div className="p-4 border-b">
              <h3 className="text-base sm:text-lg font-semibold">Profit by Item</h3>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-medium text-gray-600">Item</th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-medium text-gray-600 hidden sm:table-cell">Qty</th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-medium text-gray-600 hidden sm:table-cell">Revenue</th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-medium text-gray-600 hidden sm:table-cell">Cost</th>
                  <th className="px-3 sm:px-4 py-3 text-right text-xs sm:text-sm font-medium text-gray-600">Profit</th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-medium text-gray-600">Margin</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(data?.by_item || []).map((item, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-4 py-3">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-xs text-gray-500 sm:hidden">{item.qty} sold</p>
                    </td>
                    <td className="px-3 sm:px-4 py-3 hidden sm:table-cell">{item.qty}</td>
                    <td className="px-3 sm:px-4 py-3 hidden sm:table-cell">{formatCurrency(item.revenue)}</td>
                    <td className="px-3 sm:px-4 py-3 text-red-600 hidden sm:table-cell">{formatCurrency(item.cost)}</td>
                    <td className="px-3 sm:px-4 py-3 text-green-600 font-medium text-right">{formatCurrency(item.profit)}</td>
                    <td className="px-3 sm:px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${item.margin >= 50 ? 'bg-green-100 text-green-700' : item.margin >= 30 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                        {item.margin}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
